import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  IconButton,
  Icons,
  iconSize,
  Switch,
  Typography,
  webFonts,
  webPalette,
} from '@munifocus/ui';
import { useCookieConsent, type CookiePreferences } from '../providers/CookieConsentProvider';

export function CookiePreferencesModal() {
  const { t } = useTranslation();
  const {
    preferences,
    preferencesOpen,
    closePreferences,
    updatePreferences,
    acceptAll,
    rejectAll,
  } = useCookieConsent();
  const [local, setLocal] = useState<CookiePreferences>(preferences);

  // Re-sync the local draft whenever the dialog (re)opens.
  useEffect(() => {
    if (preferencesOpen) setLocal(preferences);
  }, [preferencesOpen, preferences]);

  const handleSave = () => {
    updatePreferences({ analytics: local.analytics });
    closePreferences();
  };
  const handleAcceptAll = () => {
    acceptAll();
    closePreferences();
  };
  const handleRejectAll = () => {
    rejectAll();
    closePreferences();
  };

  return (
    <Dialog
      open={preferencesOpen}
      onClose={closePreferences}
      maxWidth="sm"
      fullWidth
      aria-labelledby="cookie-modal-title"
      slotProps={{
        paper: {
          sx: {
            backgroundColor: webPalette.cream,
            borderRadius: '14px',
            p: { xs: 3, md: '40px 44px' },
            maxWidth: 640,
            position: 'relative',
          },
        },
        backdrop: {
          sx: { backgroundColor: webPalette.overlayBackdrop, backdropFilter: 'blur(4px)' },
        },
      }}
    >
      <IconButton
        aria-label={t('web.cookies.modal.close')}
        onClick={closePreferences}
        sx={{ position: 'absolute', top: 14, right: 14, color: webPalette.mutedSoft }}
      >
        <Icons.X size={iconSize.lg} />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Icons.Cookie size={iconSize.xl} color={webPalette.tealInk} aria-hidden />
        <Typography
          id="cookie-modal-title"
          component="h2"
          sx={{
            fontFamily: webFonts.display,
            fontWeight: 700,
            fontSize: 24,
            color: webPalette.ink,
          }}
        >
          {t('web.cookies.modal.title')}
        </Typography>
      </Box>

      <Typography sx={{ color: webPalette.text, fontSize: 15, lineHeight: 1.6, mb: 3 }}>
        {t('web.cookies.modal.description')}
      </Typography>

      <CategoryRow
        icon={<Icons.ShieldCheck size={iconSize.lg} color={webPalette.text} aria-hidden />}
        title={t('web.cookies.categories.essential.title')}
        description={t('web.cookies.categories.essential.description')}
        control={
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: webPalette.tealInk,
              whiteSpace: 'nowrap',
            }}
          >
            {t('web.cookies.modal.alwaysActive')}
          </Box>
        }
      />
      <CategoryRow
        icon={<Icons.BarChart3 size={iconSize.lg} color={webPalette.text} aria-hidden />}
        title={t('web.cookies.categories.analytics.title')}
        description={t('web.cookies.categories.analytics.description')}
        control={
          <Switch
            checked={local.analytics}
            onChange={(e) => setLocal((prev) => ({ ...prev, analytics: e.target.checked }))}
            slotProps={{ input: { 'aria-label': t('web.cookies.categories.analytics.title') } }}
          />
        }
      />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.25,
          mt: 3,
          justifyContent: 'flex-end',
        }}
      >
        <GhostButton onClick={handleRejectAll}>{t('web.cookies.actions.rejectAll')}</GhostButton>
        <GhostButton onClick={handleAcceptAll}>{t('web.cookies.actions.acceptAll')}</GhostButton>
        <SolidButton onClick={handleSave}>{t('web.cookies.actions.savePreferences')}</SolidButton>
      </Box>
    </Dialog>
  );
}

function CategoryRow({
  icon,
  title,
  description,
  control,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  control: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        border: `1px solid ${webPalette.line}`,
        borderRadius: '10px',
        backgroundColor: webPalette.white,
        p: 2,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
        <Box sx={{ mt: '2px' }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: webPalette.ink, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: webPalette.text }}>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', minHeight: 24 }}>
        {control}
      </Box>
    </Box>
  );
}

function GhostButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        color: webPalette.ink,
        backgroundColor: 'transparent',
        border: `1px solid ${webPalette.borderOnLight}`,
        borderRadius: '6px',
        px: 2,
        py: 1,
        transition: 'all 0.2s',
        '&:hover': { borderColor: webPalette.tealInk, color: webPalette.tealInk },
      }}
    >
      {children}
    </Box>
  );
}

function SolidButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        color: webPalette.onDark,
        backgroundColor: webPalette.green,
        border: '1px solid transparent',
        borderRadius: '6px',
        px: 2,
        py: 1,
        transition: 'all 0.2s',
        '&:hover': { backgroundColor: webPalette.greenDeep },
      }}
    >
      {children}
    </Box>
  );
}
